import React, { CSSProperties, ReactNode } from 'react'

import { Heading, Text, theme } from '@hhgtech/hhg-components'
import Link from 'next/link'

import {
  StyledCard,
  StyledAuthor,
  StyledExpertName,
  StyledAnchor,
} from './index.styled'

type CardAuthorProps = {
  avatarImg: JSX.Element
  authorName: ReactNode
  authorLink?: string
  editorName?: string
  editorLink?: string
  size: string
  uploadTime?: string
}

const CardAuthor = ({
  avatarImg,
  authorName,
  authorLink,
  uploadTime,
  size,
}: CardAuthorProps) => {
  return (
    <StyledAuthor>
      <div className="author">
        <div className="avatar" data-size={size}>
          {avatarImg}
        </div>
        <div className="poster">
          <Text size={size === 'lg' ? 'p3' : 'p4'}>
            <Link prefetch={false} href={authorLink || ''}>
              {authorName} •&nbsp;
            </Link>
            <span>{uploadTime}</span>
          </Text>
        </div>
      </div>
    </StyledAuthor>
  )
}

type ArticleExpertProps = {
  isMobile: boolean
  avatarImg: JSX.Element
  name: ReactNode
  link?: string
  size: 'lg' | 'md' | 'sm'
  specialty: string
}

const CardExpert = ({
  isMobile,
  avatarImg,
  name,
  link,
  size = 'lg',
  specialty,
}: ArticleExpertProps) => {
  return (
    <StyledAuthor>
      <div className="author">
        <div className="expert-avatar" data-size={size}>
          {avatarImg}
        </div>
        <div className="expert">
          {isMobile ? (
            <Link href={link || ''} prefetch={false}>
              {size === 'lg' ? (
                <>
                  <Heading
                    tag="h6"
                    className="expert__name"
                    color={theme.colors.gray600}
                  >
                    {name}
                  </Heading>
                  <Text
                    color={theme.colors.gray500}
                    size="p3"
                    className="specialty"
                  >
                    {specialty}
                  </Text>
                </>
              ) : (
                <>
                  <Heading
                    tag="h6"
                    className="expert__name"
                    color={theme.colors.gray600}
                  >
                    {name}
                  </Heading>
                  <Text
                    color={theme.colors.gray500}
                    size="p3"
                    className="specialty"
                  >
                    {specialty}
                  </Text>
                </>
              )}
            </Link>
          ) : (
            <Link href={link || ''} prefetch={false}>
              {size === 'lg' ? (
                <>
                  <Heading
                    tag="h6"
                    className="expert__name"
                    color={theme.colors.gray600}
                  >
                    {name}
                  </Heading>
                  <Text
                    color={theme.colors.gray500}
                    size="p3"
                    className="specialty"
                  >
                    {specialty}
                  </Text>
                </>
              ) : (
                <>
                  <StyledExpertName
                    color={theme.colors.gray600}
                    className="expert__name"
                  >
                    {name}
                  </StyledExpertName>
                  <Text
                    color={theme.colors.gray500}
                    size="p4"
                    className="specialty"
                  >
                    {specialty}
                  </Text>
                </>
              )}
            </Link>
          )}
        </div>
      </div>
    </StyledAuthor>
  )
}

export type Props = {
  banner: JSX.Element
  title: string
  text: string
  avatarImg: JSX.Element
  direction?: 'vertical' | 'horizontal'
  size?: 'sm' | 'lg' | 'md'
  type?: 'author' | 'expert'
  section?: 'home' | 'normal'
  authorName: ReactNode
  authorLink?: string
  specialty?: string
  uploadTime?: string
  categoryName?: string
  categoryLink?: string
  articleLink?: string
  isBookmarkDisplayed?: boolean
  onBookmark?: () => void
  isBookmark?: boolean
  hasButton?: boolean
  textButton?: string
  isMobile?: boolean
  styleBanner?: CSSProperties
  styleLabel?: CSSProperties
  styleTitle?: CSSProperties
  style?: CSSProperties
  className?: string
  dataEventCategoryForCategory?: string
  dataEventActionForCategory?: string
  dataEventLabelForCategory?: string
  dataEventCategoryForTitle?: string
  dataEventActionForTitle?: string
  dataEventLabelForTitle?: string
  dataEventCategoryForBanner?: string
  dataEventActionForBanner?: string
  dataEventLabelForBanner?: string
  dataEventCategoryForBookmark?: string
  dataEventActionForBookmark?: string
  dataEventLabelForBookmark?: string
  onClickCategoryName?: (e: React.MouseEvent) => void
}

const ExpertArticleCard = ({
  isMobile = true,
  direction = 'horizontal',
  size = 'md',
  banner,
  isBookmarkDisplayed = false,
  type = 'author',
  section = 'normal',
  onBookmark,
  categoryName,
  styleLabel,
  title,
  styleTitle,
  text,
  specialty,
  avatarImg,
  authorName,
  authorLink,
  articleLink,
  uploadTime,
  isBookmark = false,
  className,
  style,
  categoryLink,
  styleBanner,
  dataEventCategoryForCategory,
  dataEventActionForCategory,
  dataEventLabelForCategory,
  dataEventCategoryForTitle,
  dataEventActionForTitle,
  dataEventLabelForTitle,
  dataEventCategoryForBanner,
  dataEventActionForBanner,
  dataEventLabelForBanner,
  dataEventCategoryForBookmark,
  dataEventActionForBookmark,
  dataEventLabelForBookmark,
  onClickCategoryName,
}: Props) => {
  const handleClick = (e: any) => {
    e.stopPropagation()
    onBookmark && onBookmark()
  }

  const headingTag =
    size === 'lg' && type === 'expert'
      ? 'h4'
      : size === 'lg' && section === 'home'
      ? isMobile
        ? 'h4'
        : 'h3'
      : size === 'lg'
      ? 'h4'
      : 'h5'
  const descriptionTag = size === 'lg' ? 'p3' : 'p4'
  const categoryLabelTag =
    !isMobile && section === 'home' && size === 'lg' && type === 'author'
      ? 'c1'
      : 'c2'

  const onClickCategory = (e: any) => {
    onClickCategoryName && onClickCategoryName(e)
  }

  return (
    <StyledCard
      className={className}
      data-size={size}
      style={style}
      data-direction={direction}
      data-is-expert={type === 'expert' || undefined}
    >
      <div className="banner" style={styleBanner}>
        <StyledAnchor
          href={articleLink || '#'}
          data-event-category={dataEventCategoryForBanner}
          data-event-action={dataEventActionForBanner}
          data-event-label={dataEventLabelForBanner}
        >
          {banner}
        </StyledAnchor>
      </div>
      <div className="content">
        <div className="inner-content">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Text
              style={styleLabel}
              className="label"
              size={categoryLabelTag}
              type="caption"
            >
              <StyledAnchor
                data-event-category={dataEventCategoryForCategory}
                data-event-action={dataEventActionForCategory}
                data-event-label={dataEventLabelForCategory}
                href={categoryLink || '#'}
                onClick={onClickCategory}
              >
                {categoryName}
              </StyledAnchor>
            </Text>
            {isBookmarkDisplayed && (
              <a
                data-event-category={dataEventCategoryForBookmark}
                data-event-action={dataEventActionForBookmark}
                data-event-label={dataEventLabelForBookmark}
                className="bookmark"
                onClick={handleClick}
              >
                {!isBookmark ? (
                  <svg width="14" height="18" viewBox="0 0 14 18" fill="none">
                    <path
                      d="M13 17L7 12.5556L1 17V2.77778C1 2.30628 1.18061 1.8541 1.5021 1.5207C1.82359 1.1873 2.25963 1 2.71429 1H11.2857C11.7404 1 12.1764 1.1873 12.4979 1.5207C12.8194 1.8541 13 2.30628 13 2.77778V17Z"
                      stroke="#262626"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="14" height="18" viewBox="0 0 14 18" fill="none">
                    <path
                      d="M13 17L7 12.5556L1 17V2.77778C1 2.30628 1.18061 1.8541 1.5021 1.5207C1.82359 1.1873 2.25963 1 2.71429 1H11.2857C11.7404 1 12.1764 1.1873 12.4979 1.5207C12.8194 1.8541 13 2.30628 13 2.77778V17Z"
                      fill="#2ED69E"
                      stroke="#2ED69E"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </a>
            )}
          </div>

          <Heading
            tag={headingTag}
            style={styleTitle}
            className="title"
            as={size === 'lg' ? 'h2' : 'h3'}
          >
            <StyledAnchor
              href={articleLink || '#'}
              data-event-category={dataEventCategoryForTitle}
              data-event-action={dataEventActionForTitle}
              data-event-label={dataEventLabelForTitle}
            >
              {title}
            </StyledAnchor>
          </Heading>

          {text && (
            <Link href={articleLink || '#'} prefetch={false}>
              <Text size={descriptionTag} className="text">
                {text}
              </Text>
            </Link>
          )}
        </div>

        <div className="footer">
          {type === 'author' ? (
            <CardAuthor
              size={size}
              avatarImg={avatarImg}
              authorName={authorName}
              authorLink={authorLink}
              uploadTime={uploadTime}
            />
          ) : (
            <CardExpert
              size={size}
              isMobile={isMobile}
              avatarImg={avatarImg}
              specialty={specialty || ''}
              name={authorName}
              link={authorLink}
            />
          )}
        </div>
      </div>
    </StyledCard>
  )
}

export { ExpertArticleCard }
